import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import LoadingState from "@/components/LoadingState";
import PullToRefreshIndicator from "@/components/PullToRefreshIndicator";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  HelpCircle,
  Building,
  User,
  Camera,
  RefreshCw,
  FileText,
  Eye,
  Archive,
  MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import type { GrievanceUpdate } from "@/data/grievances";
import {
  useCase,
  useCaseUpdates,
  useCaseImages,
} from "@/services/queries";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const hasAutoSelectedUpdatesTab = useRef(false);

  const {
    data: grievance,
    isLoading,
    isError,
    refetch: refetchGrievance,
  } = useCase(id, !!id);

  const {
    data: updates,
    isLoading: isUpdateLoading,
    refetch: refetchUpdates,
  } = useCaseUpdates(id, !!id);

  const imageIds =
    grievance?.images?.map((img) => ({
      id: typeof img === "string" ? img : (img as { id: string }).id,
    })) ?? [];
  const { data: images, refetch: refetchImages } = useCaseImages(imageIds, imageIds.length > 0);

  const pullToRefresh = usePullToRefresh({
    onRefresh: async () => {
      await Promise.all([
        refetchGrievance(),
        refetchUpdates(),
        imageIds.length > 0 ? refetchImages() : Promise.resolve(),
      ]);
    },
    enabled: !!id,
  });

  useEffect(() => {
    setImageErrors(new Set());
  }, [id]);

  useEffect(() => {
    if (!id) {
      toast({
        title: "Case not found",
        description: `No case ID provided in the URL.`,
        variant: "destructive",
      });
      return;
    }

    if (images && images.length > 0) {
      setSelectedImage(images[0]?.url ?? null);
    }
    if (!isLoading && !grievance) {
      toast({
        title: "Case not found",
        description: `We couldn't find the case with ID: ${id}`,
        variant: "destructive",
      });
    }
  }, [id, grievance, isLoading, toast, images]);

  // When there are updates, show the Updates tab first so users see backend comments/status changes immediately
  useEffect(() => {
    if (updates && updates.length > 0 && !hasAutoSelectedUpdatesTab.current) {
      setActiveTab("updates");
      hasAutoSelectedUpdatesTab.current = true;
    }
  }, [updates]);

  useEffect(() => {
    if (notFound && !isLoading) {
      navigate("/");
    }
  }, [notFound, isLoading, navigate]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusIcon = () => {
    if (!grievance) return null;

    switch (grievance.status) {
      case "pending":
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case "investigating":
        return <Clock className="w-5 h-5 text-blue-500" />;
      case "resolved":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "done":
        return <HelpCircle className="w-5 h-5 text-slate-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
    }
  };

  const getCategoryColor = () => {
    if (!grievance) return "";

    switch (grievance.category) {
      case "environmental":
        return "bg-emerald-50 text-emerald-800 border-emerald-200";
      case "health":
        return "bg-red-50 text-red-800 border-red-200";
      case "property":
        return "bg-indigo-50 text-indigo-800 border-indigo-200";
      case "social":
        return "bg-amber-50 text-amber-800 border-amber-200";
      case "other":
        return "bg-gray-50 text-gray-800 border-gray-200";
      default:
        return "bg-gray-50 text-gray-800 border-gray-200";
    }
  };

  const navigateToAddCase = () => {
    if (id) {
      navigate(`/add-case`);
    }
  };

  const navigateToUpdates = () => {
    if (id) {
      navigate(`/case/${id}/updates`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header title="Case Details" showBackButton transparent />
        <div className="pt-20">
          <LoadingState />
        </div>
      </div>
    );
  }

  if (!grievance) {
    return null;
  }

  return (
    <div
      ref={pullToRefresh.containerRef}
      className="min-h-screen bg-background overflow-auto"
    >
      <PullToRefreshIndicator
        isVisible={pullToRefresh.shouldShowIndicator}
        isRefreshing={pullToRefresh.isRefreshing}
        pullDistance={pullToRefresh.pullDistance}
      />

      <Header title="Case Details" showBackButton transparent />

      <main className="px-4 pt-20 pb-20">
        <div className="space-y-6">
          {/* Case header */}
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <Badge
                className={cn("px-3 py-1 rounded-full", getCategoryColor())}
              >
                {grievance.category.charAt(0).toUpperCase() +
                  grievance.category.slice(1)}
              </Badge>
              <Badge
                variant="outline"
                className="flex items-center gap-1 px-3 py-1 rounded-full"
              >
                {getStatusIcon()}
                <span className="first-letter:uppercase">
                  {grievance.status}
                </span>
              </Badge>
            </div>

            <h1 className="text-2xl font-bold">{grievance.title}</h1>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-muted-foreground">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-1.5" />
                {grievance.location}
              </div>
              <div className="hidden sm:block text-muted-foreground">•</div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1.5" />
                {formatDate(grievance.date)}
              </div>
            </div>
          </div>

          {/* Updates callout: clear cue that this case has activity (comments/progress from team) */}
          {updates && updates.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveTab("updates")}
              className={cn(
                "w-full text-left rounded-lg border px-4 py-3 flex items-center gap-3",
                "bg-primary/5 border-primary/20 hover:bg-primary/10 hover:border-primary/30",
                "transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              )}
              aria-label={`${updates.length} update${updates.length !== 1 ? "s" : ""} — view comments and progress`}
            >
              <span className="flex-shrink-0 rounded-full bg-primary/10 p-2" aria-hidden>
                <MessageSquare className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground">
                  {updates.length} update{updates.length !== 1 ? "s" : ""}
                </p>
                <p className="text-sm text-muted-foreground">
                  Comments and progress from the team
                </p>
              </div>
              <Eye className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />
            </button>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 gap-2 h-auto py-3"
              onClick={navigateToAddCase}
            >
              <Camera size={18} />
              <span>New Case</span>
            </Button>

            <Button
              variant="outline"
              className="flex-1 gap-2 h-auto py-3"
              onClick={navigateToUpdates}
            >
              {grievance?.status === "resolved" ? (
                <>
                  <Archive size={18} />
                  <span>View Case History</span>
                </>
              ) : (
                <>
                  <Eye size={18} />
                  <span>View Updates</span>
                </>
              )}
            </Button>
          </div>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details" className="text-xs sm:text-sm">
                Details
              </TabsTrigger>
              <TabsTrigger value="photos" className="text-xs sm:text-sm">
                Photos
              </TabsTrigger>
              <TabsTrigger value="updates" className="text-xs sm:text-sm">
                Updates
                {updates && updates.length > 0 && (
                  <span className="ml-1.5 text-xs font-medium tabular-nums">
                    ({updates.length})
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4 pt-4">
              <Card>
                <CardContent className="p-4">
                  <h2 className="text-base font-semibold mb-2">Description</h2>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {grievance.description}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h2 className="text-base font-semibold mb-2">Impact</h2>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {grievance.impact}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h2 className="text-base font-semibold mb-3">
                    Case Information
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center text-xs text-muted-foreground mb-1">
                        <Building className="w-3.5 h-3.5 mr-1.5" />
                        Company Involved
                      </div>
                      <p className="text-sm font-medium">
                        {grievance.companyInvolved}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center text-xs text-muted-foreground mb-1">
                        <User className="w-3.5 h-3.5 mr-1.5" />
                        Reported By
                      </div>
                      <p className="text-sm font-medium">
                        {grievance.reportedBy}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center text-xs text-muted-foreground mb-1">
                        <FileText className="w-3.5 h-3.5 mr-1.5" />
                        Case ID
                      </div>
                      <p className="text-sm font-medium">
                        {grievance.displayId}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Photos Tab */}
            <TabsContent value="photos" className="space-y-4 pt-4">
              {images && images.length > 0 ? (
                <div className="space-y-4">
                  <div className="aspect-video max-h-[70vh] max-w-full mx-auto rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                    {(selectedImage || images[0]?.url) &&
                     !imageErrors.has(selectedImage || images[0]?.url || "") ? (
                      <img
                        src={selectedImage || images[0]?.url}
                        alt={grievance.title}
                        className="w-full h-full object-contain"
                        onError={() =>
                          setImageErrors((prev) =>
                            new Set(prev).add(selectedImage || images[0]?.url || "")
                          )
                        }
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-muted-foreground p-4">
                        <Camera className="h-12 w-12 mb-2" />
                        <span className="text-sm">Image unavailable</span>
                      </div>
                    )}
                  </div>

                  {images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {images.map((image, index: number) => {
                        const url = image.url;
                        const failed = url && imageErrors.has(url);
                        return (
                          <button
                            key={image.id || index}
                            onClick={() => url && !failed && setSelectedImage(url)}
                            className={cn(
                              "relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200",
                              selectedImage === url ||
                                (!selectedImage && index === 0)
                                ? "border-primary"
                                : "border-transparent hover:border-primary/50"
                            )}
                          >
                            {url && !failed ? (
                              <img
                                src={url}
                                alt={`View ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={() =>
                                  setImageErrors((prev) =>
                                    new Set(prev).add(url)
                                  )
                                }
                              />
                            ) : (
                              <div className="w-full h-full bg-muted flex items-center justify-center">
                                <Camera className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 space-y-4">
                  <div className="mx-auto bg-muted rounded-full w-16 h-16 flex items-center justify-center">
                    <Camera className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-medium">No Photos Available</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      There are no photos for this case.
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Updates Tab */}
            <TabsContent value="updates" className="space-y-4 pt-4">
              {updates && updates.length > 0 ? (
                <div className="space-y-4">
                  <div className="space-y-4">
                    {updates.map((update: GrievanceUpdate, index: number) => (
                      <Card key={index}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm font-medium">
                              {formatDate(update.date)}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground whitespace-pre-line">
                            {update.content}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    onClick={navigateToUpdates}
                    className="w-full"
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View Full Update History
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12 space-y-4">
                  <div className="mx-auto bg-muted rounded-full w-16 h-16 flex items-center justify-center">
                    <Eye className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-medium">No Updates Available</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      There are no updates for this case yet.
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default CaseDetail;
